import { useState, useEffect } from "react";
import { Info, Clapperboard, Star, Link2, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FormDialog } from "@/components/ui/form-dialog";
import { FormSection } from "@/components/ui/form-section";
import { useUpdateMovie } from "@/hooks/useMovies";
import { toast } from "sonner";
import type { Movie } from "@/types";
import { MovieImageManager } from "./MovieImageManager";

interface MovieEditDialogProps {
  movie: Movie | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MovieEditDialog({ movie, open, onOpenChange }: MovieEditDialogProps) {
  const updateMovie = useUpdateMovie();

  const [form, setForm] = useState({
    title: "",
    originalTitle: "",
    description: "",
    year: "",
    runtime: "",
    filmwebRating: "",
    imdbRating: "",
    rtScore: "",
    filmwebUrl: "",
    ticketUrl: "",
    countryCode: "",
    room: "",
  });

  useEffect(() => {
    if (movie) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setForm({
        title: movie.title,
        originalTitle: movie.originalTitle ?? "",
        description: movie.description ?? "",
        year: movie.year != null ? String(movie.year) : "",
        runtime: movie.runtime != null ? String(movie.runtime) : "",
        filmwebRating: movie.filmwebRating ?? "",
        imdbRating: movie.imdbRating ?? "",
        rtScore: movie.rtScore ?? "",
        filmwebUrl: movie.filmwebUrl ?? "",
        ticketUrl: movie.ticketUrl ?? "",
        countryCode: movie.countryCode ?? "",
        room: movie.room ?? "",
      });
    }
  }, [movie]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!movie) return;
    try {
      await updateMovie.mutateAsync({
        id: movie.id,
        body: {
          title: form.title,
          originalTitle: form.originalTitle || undefined,
          description: form.description || undefined,
          year: form.year ? parseInt(form.year) : null,
          runtime: form.runtime ? parseInt(form.runtime) : null,
          filmwebRating: form.filmwebRating || null,
          imdbRating: form.imdbRating || null,
          rtScore: form.rtScore || null,
          filmwebUrl: form.filmwebUrl || null,
          ticketUrl: form.ticketUrl || null,
          countryCode: form.countryCode || null,
          room: form.room || null,
        },
      });
      toast.success("Film zaktualizowany");
      onOpenChange(false);
    } catch (err) {
      toast.error("Nie udało się zaktualizować filmu", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const f =
    (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value }));

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Edytuj film"
      subtitle={movie?.id}
      size="lg"
      footer={
        <>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button type="submit" form="movie-edit-form" disabled={updateMovie.isPending}>
            {updateMovie.isPending ? "Zapisywanie..." : "Zapisz zmiany"}
          </Button>
        </>
      }
    >
      <form id="movie-edit-form" onSubmit={handleSubmit} className="space-y-4">
        <FormSection icon={Info} title="Podstawowe informacje">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Tytuł (PL)</Label>
              <Input value={form.title} onChange={f("title")} required />
            </div>
            <div className="space-y-2">
              <Label>Tytuł oryginalny</Label>
              <Input
                value={form.originalTitle}
                onChange={f("originalTitle")}
                placeholder="Opcjonalnie"
              />
            </div>
            <div className="space-y-2">
              <Label>Opis</Label>
              <Textarea value={form.description} onChange={f("description")} rows={4} />
            </div>
          </div>
        </FormSection>

        <FormSection icon={Clapperboard} title="Szczegóły seansu">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Rok</Label>
              <Input type="number" value={form.year} onChange={f("year")} placeholder="np. 2024" />
            </div>
            <div className="space-y-2">
              <Label>Czas trwania (min)</Label>
              <Input
                type="number"
                value={form.runtime}
                onChange={f("runtime")}
                placeholder="np. 120"
              />
            </div>
            <div className="space-y-2">
              <Label>Kraj</Label>
              <Input
                value={form.countryCode}
                onChange={f("countryCode")}
                placeholder="np. PL, US"
              />
            </div>
            <div className="space-y-2">
              <Label>Sala</Label>
              <Input value={form.room} onChange={f("room")} placeholder="np. Sala 1" />
            </div>
          </div>
        </FormSection>

        <FormSection icon={Star} title="Oceny">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label>Filmweb</Label>
              <Input
                value={form.filmwebRating}
                onChange={f("filmwebRating")}
                placeholder="np. 7.5"
              />
            </div>
            <div className="space-y-2">
              <Label>IMDb</Label>
              <Input value={form.imdbRating} onChange={f("imdbRating")} placeholder="np. 7.2" />
            </div>
            <div className="space-y-2">
              <Label>RT</Label>
              <Input value={form.rtScore} onChange={f("rtScore")} placeholder="np. 85%" />
            </div>
          </div>
        </FormSection>

        <FormSection icon={Link2} title="Linki">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>URL Filmweb</Label>
              <Input
                value={form.filmwebUrl}
                onChange={f("filmwebUrl")}
                placeholder="https://filmweb.pl/..."
              />
            </div>
            <div className="space-y-2">
              <Label>Link do zakupu biletów</Label>
              <Input value={form.ticketUrl} onChange={f("ticketUrl")} placeholder="https://..." />
            </div>
          </div>
        </FormSection>

        {movie && (
          <FormSection icon={ImageIcon} title="Zdjęcie">
            <MovieImageManager movieId={movie.id} />
          </FormSection>
        )}
      </form>
    </FormDialog>
  );
}
